import { useEffect } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import { useUserContext } from '@hooks/context/useUserContext';
import { useForm } from '@hooks/helpers/useForm';
import { FormProvider } from 'react-hook-form';
import PersonalSection from '@sections/settings/profile/personal';
import AddressSection from '@sections/settings/profile/address';
import { zodResolver } from '@hookform/resolvers/zod';
import { profileSettingsValidation } from '@helpers/validation_objects/profile_settings';
import {
    EAddressType,
    EUserType,
    TBusinessAddress,
    TIndividualAddress,
    TIndividualProfile
} from '@xeppt/xeppt-sdk/types/user';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiUserService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import AvatarSection from '@sections/settings/profile/avatar';
import Button from '@components/common/button';
import { routes } from '@const/routes';
import GeneralSettings from '@sections/settings/profile/general';
import Contacts from '@sections/settings/profile/contacts';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import BusinessSection from '@sections/settings/profile/business';
import BusinessAddressSection from '@sections/settings/profile/business_address';

const ProfileLayout = () => {
    // const { t } = useTranslation('sections', {
    //     keyPrefix: 'settings.personal'
    // });
    const { user } = useUserContext();
    const { validationLocale, submitLocale } = useLocales();
    const { handleBack } = useNavigationContext();
    const formMethods = useForm<TIndividualProfile>({
        resolver: zodResolver(
            profileSettingsValidation({
                messages: {
                    first_name: validationLocale('first_name'),
                    last_name: validationLocale('last_name'),
                    birth_date: validationLocale('birth_date'),
                    occupancy: validationLocale('occupancy'),
                    tag: validationLocale('tag'),
                    timezone: validationLocale('timezone')
                }
            })
        )
    });

    useEffect(() => {
        if (user) {
            formMethods.reset(user);
        }
    }, [user]);

    const { data } = useApiQuery({
        method: () => apiUserService.getAddress()
    });

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => handleBack(routes.settings)}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">Profile</Typography>
            </div>
            <FormProvider {...formMethods}>
                <div className={styles.content}>
                    <AvatarSection type="user" />
                    <div className={styles.container}>
                        <PersonalSection />
                        <Contacts />
                        {user?.type === EUserType.BUSINESS && <BusinessSection />}
                        <GeneralSettings />
                        <AddressSection
                            name="billing_address"
                            type={EAddressType.BILLING}
                            data={data as TIndividualAddress}
                        />
                        {user?.type === EUserType.BUSINESS && (
                            <BusinessAddressSection data={data as TBusinessAddress} />
                        )}
                    </div>
                </div>
            </FormProvider>
        </motion.div>
    );
};

export default ProfileLayout;
